import { computed, DestroyRef, Injectable, InjectionToken, inject, signal } from '@angular/core';
import { defer, finalize, isObservable, type Observable } from 'rxjs';

export interface MmscanLoadingOptions {
  /** Work that finishes faster than this (ms) never shows the indicator, avoiding flicker. */
  delay: number;
}

export const MMSCAN_LOADING_OPTIONS = new InjectionToken<MmscanLoadingOptions>('MMSCAN_LOADING_OPTIONS', {
  providedIn: 'root',
  factory: () => ({ delay: 150 }),
});

/**
 * App-wide "something is loading" state. HTTP requests are counted by `mmscanLoadingInterceptor`;
 * other work can be tracked with `track()` or `start()`. `mmscan-app-shell` shows a progress
 * bar under its header while `visible()` is true.
 *
 * ```ts
 * const data = await this.loading.track(this.worker.run());
 * ```
 */
@Injectable({ providedIn: 'root' })
export class MmscanLoadingService {
  private readonly options = inject(MMSCAN_LOADING_OPTIONS);
  private readonly pending = signal(0);
  private readonly shown = signal(false);
  private timer?: ReturnType<typeof setTimeout>;

  /** Something is in progress (immediately). */
  readonly active = computed(() => this.pending() > 0);
  /** Something has been in progress for longer than `delay`: show the indicator. */
  readonly visible = computed(() => this.active() && this.shown());

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.timer));
  }

  /** Marks work as started; call the returned function (once) when it ends. */
  start(): () => void {
    this.pending.update((count) => count + 1);
    if (this.pending() === 1) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.shown.set(true), this.options.delay);
    }
    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      this.pending.update((count) => Math.max(0, count - 1));
      if (this.pending() === 0) {
        clearTimeout(this.timer);
        this.shown.set(false);
      }
    };
  }

  /** Tracks a promise or observable until it settles / completes / is unsubscribed. */
  track<T>(work: Promise<T>): Promise<T>;
  track<T>(work: Observable<T>): Observable<T>;
  track<T>(work: Promise<T> | Observable<T>): Promise<T> | Observable<T> {
    if (isObservable(work)) {
      // Counted per subscription, like an HTTP request.
      return defer(() => work.pipe(finalize(this.start())));
    }
    return work.finally(this.start());
  }
}
